import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren, OnChanges, SimpleChanges } from '@angular/core';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import * as ace from "ace-builds";
import { AssessmentService } from '../../assessments/assessment.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { configureAceSession } from 'src/app/shared/ace-mode-utils';

@Component({
  selector: 'app-questions-preview',
  templateUrl: './questions-preview.component.html',
  styleUrls: ['./questions-preview.component.scss']
})

export class QuestionsPreviewComponent implements OnInit, OnChanges {

  @ViewChildren("previewCodingQuestionEditor") private previewCodingQuestionEditor: QueryList<ElementRef>;

  @Input() previewQuestions: any = {
    codingQuestionsList: [],
    multipleChoiceQuestionList: [],
    openEndedQuestionList: []
  };
  @Input() showAnswers: boolean = false;
  @Input() assessmentId: string;
  selectedLang: number = 0;
  previewQuestionCodeAceEditor: any;
  testCaseResults = {
    success: 0,
    failure: 0,
    errored: 0
  };
  config: AngularEditorConfig = {
    height: 'auto',
    minHeight: '0',
    width: 'auto',
    minWidth: '0',
    showToolbar: false
  };
  answeredLang: string = '';
  // Removed mcqCorrectCount and mcqTotalCount as they are no longer needed

  constructor(
    private assessmentService: AssessmentService,
    private snackBar: MatSnackBar
  ) { }

  /**
   * Convert escaped newlines (\n as text) to actual newlines, except for C/C++
   */
  private convertEscapedNewlines(code: string, codeLanguage?: string, languageId?: number): string {
    if (!code) return '';
    const lang = String(codeLanguage || '').toLowerCase();
    const isCandCpp = [50, 54].includes(Number(languageId)) || /\bc\+\+|cpp\b|^c$/.test(lang);
    if (isCandCpp) return code;
    return code.replace(/\\n/g, '\n');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.previewQuestions && this.previewQuestions) {
      setTimeout(() => {
        const codingIndex = this.getCodingTabIndex();
        if (codingIndex === 0) {
          this.selectedTabChange(0);
        }
      }, 500);
    }
  }

  ngOnInit(): void {
    // Component initialization
  }

  hasAnyQuestions(): boolean {
    return this.hasMcqQuestions() || this.hasOpenEndedQuestions() || this.hasCodingQuestions();
  }

  hasMcqQuestions(): boolean {
    return Array.isArray(this.previewQuestions?.multipleChoiceQuestionList) && this.previewQuestions.multipleChoiceQuestionList.length > 0;
  }

  hasOpenEndedQuestions(): boolean {
    return Array.isArray(this.previewQuestions?.openEndedQuestionList) && this.previewQuestions.openEndedQuestionList.length > 0;
  }

  hasCodingQuestions(): boolean {
    return Array.isArray(this.previewQuestions?.codingQuestionsList) && this.previewQuestions.codingQuestionsList.length > 0;
  }

  getCodingTabIndex(): number {
    if (!this.hasCodingQuestions()) return -1;
    let index = 0;
    if (this.hasMcqQuestions()) index++;
    if (this.hasOpenEndedQuestions()) index++;
    return index;
  }

  downloadAnswersheet(): void {
    if (!this.assessmentId) {
      this.snackBar.open('Assessment ID not available', 'Close', { duration: 3000 });
      return;
    }

    this.assessmentService.downloadAnswers(this.assessmentId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `assessment-answers-${this.assessmentId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Answer sheet downloaded successfully!', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error downloading answer sheet:', error);
        this.snackBar.open('Failed to download answer sheet', 'Close', { duration: 3000 });
      }
    });
  }

  copyCode(cqIdx: number): void {
    const editor = ace.edit(`preview-ace-editor-${cqIdx}`);
    const code = editor ? editor.getValue() : '';
    if (!code) {
      this.snackBar.open('No code to copy', 'Close', { duration: 2000 });
      return;
    }
    navigator.clipboard.writeText(code).then(() => {
      this.snackBar.open('Code copied to clipboard!', 'Close', { duration: 2000 });
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.snackBar.open('Code copied to clipboard!', 'Close', { duration: 2000 });
    });
  }

  showFullQuestion(idx) {
    const editor: any = ace.edit(`preview-ace-editor-${idx}`);
    const height = editor?.session?.getScreenLength() * (editor?.renderer?.lineHeight || 14) + (editor?.renderer?.scrollBar?.getWidth?.() || editor?.renderer?.scrollBar?.width || 0);
    const element = document.getElementById('preview-ace-editor-' + idx);
    if (element) {
      element.style.height = height + 10 + 'px';
    }
    editor.resize();
  }

  previewCodingQuestion(cq, cqIdx, langIdx = 0): void {
    this.selectedLang = langIdx;
    ace.config.set("fontSize", "14px");
    ace.config.set('basePath', 'https://unpkg.com/ace-builds@1.4.12/src-noconflict');
    const editorElement = document.getElementById('preview-ace-editor-' + cqIdx);
    if (!editorElement) {
      setTimeout(() => this.previewCodingQuestion(cq, cqIdx, langIdx), 100);
      return;
    }
    const editor = ace.edit(`preview-ace-editor-${cqIdx}`);
    let cQuestion = this.showAnswers ? (cq?.userAnswer || cq.questionSignatures[langIdx].initialCode) : cq.questionSignatures[langIdx].initialCode;
    const langInfo = cq.questionSignatures[langIdx]?.functionSignature;
    editor?.session?.setValue(this.convertEscapedNewlines(cQuestion, langInfo?.code_language, langInfo?.language_id));
    editor?.setTheme('ace/theme/solarized_dark');
    editor?.setReadOnly(true);
    //answerSignature
    this.testCaseResults = {
      success: this.showAnswers ? cq?.answerSignature?.answer?.run_results?.success || 0 : cq.questionSignatures[langIdx]?.run_results.success,
      failure: this.showAnswers ? cq?.answerSignature?.answer?.run_results?.failure || 0 : cq.questionSignatures[langIdx]?.run_results.failure,
      errored: this.showAnswers ? cq?.answerSignature?.answer?.run_results?.errored || 0 : cq.questionSignatures[langIdx]?.run_results.errored,
    }
    if (this.showAnswers) {
      // Set the answered language for this specific question
      cq.answeredLangId = cq?.answerSignature?.answer?.questionSignature_id;
    }
    if (this.previewQuestions?.codingQuestionsList[cqIdx]) {
      this.previewQuestions.codingQuestionsList[cqIdx].testCaseResults = this.testCaseResults;
    }
    configureAceSession(editor.session, cq.questionSignatures[langIdx]?.functionSignature?.code_language);
    this.showFullQuestion(cqIdx);
  }

  selectedTabChange(idx): void {
    const codingIndex = this.getCodingTabIndex();
    if (idx === codingIndex && codingIndex !== -1) {
      for (let index = 0; index < (this.previewQuestions?.codingQuestionsList?.length || 0); index++) {
        setTimeout(() => {
          this.previewCodingQuestion(this.previewQuestions?.codingQuestionsList[index], index);
        }, index * 500);
      }
    }
  }

  formatQuestionText(text: string): string {
    if (!text) return '';
    let formatted = text;

    // Handle escaped newlines (common in JSON data)
    formatted = formatted.replace(/\\n/g, '\n');

    // 1. Handle Code Blocks: ```code``` -> Premium Code Box
    formatted = formatted.replace(/```[a-z]*\s*([\s\S]*?)```/g, (match, p1) => {
      const code = p1.trim();
      return `<div class="code-block-premium">${code}</div>`;
    });

    // 2. Format Headers (# or ##) -> Bold
    formatted = formatted.replace(/^#+\s*(.*)$/gm, '<b>$1</b>');

    // 3. Handle Markdown bold: **text** -> <b>text</b>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');

    // 4. Handle Inline Code: `code` -> <code>code</code>
    formatted = formatted.replace(/`(.*?)`/g, '<code>$1</code>');

    // 5. Handle Bullet Points: "- " or "* " at start of line -> Bullet
    formatted = formatted.replace(/^\s*[\-\*]\s+/gm, '&bull; ');

    // 6. Handle Newlines: \n -> <br>
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted;
  }

  getScoreClass(score: number): string {
    if (score >= 8) return 'score-excellent';
    if (score >= 6) return 'score-good';
    if (score >= 4) return 'score-average';
    return 'score-poor';
  }
}
